import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Image as ImageIcon, Upload, BarChart3, Facebook, ShieldCheck } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input, Textarea } from '@/components/ui/Field';
import { Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { uploadFaviconImage } from '@/lib/siteImageApi';

// Tracking IDs are singletons (one GA4/GTM/Pixel ID for the whole site),
// not per-page or per-locale content, so — unlike every other admin
// editor in this app — they're stored/edited under locale 'en' only, no
// locale switcher needed here.
const TRACKING_FIELDS: { key: string; label: string; placeholder: string; hint: string }[] = [
  { key: 'tracking.ga4Id', label: 'Google Analytics 4 — Measurement ID', placeholder: 'G-XXXXXXXXXX', hint: 'Found in GA4 Admin → Data Streams → your stream' },
  { key: 'tracking.gtmId', label: 'Google Tag Manager — Container ID', placeholder: 'GTM-XXXXXXX', hint: 'Only needed if you manage tags through GTM instead of (or alongside) GA4 directly' },
  { key: 'tracking.metaPixelId', label: 'Meta (Facebook) Pixel ID', placeholder: '1234567890123456', hint: 'Found in Meta Events Manager → your Pixel → Settings' },
  { key: 'tracking.gscVerification', label: 'Google Search Console — Verification Code', placeholder: 'abc123...', hint: 'HTML tag method → just the content="..." value, not the whole tag' },
];

export default function AdminSeoAnalyticsPage() {
  const { push } = useToast();
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState<Record<string, string>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Record<string, 'saving' | 'saved' | undefined>>({});
  const [faviconUrl, setFaviconUrl] = useState<string>('');
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [contentRes, imagesRes] = await Promise.all([
      supabase.from('rxk_site_content').select('key, value').eq('locale', 'en').like('key', 'tracking.%'),
      supabase.from('rxk_site_images').select('key, url').eq('key', 'seo.favicon').maybeSingle(),
    ]);
    const map: Record<string, string> = {};
    for (const row of (contentRes.data as { key: string; value: string }[]) ?? []) map[row.key] = row.value;
    setValues(map);
    setDrafts(map);
    setFaviconUrl((imagesRes.data as { key: string; url: string } | null)?.url ?? '');
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (key: string, value: string) => {
    setDrafts((d) => ({ ...d, [key]: value }));
  };

  const handleBlur = async (key: string) => {
    const value = drafts[key] ?? '';
    if (value === (values[key] ?? '')) return;
    setStatus((s) => ({ ...s, [key]: 'saving' }));
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('rxk_site_content')
        .upsert({ key, locale: 'en', value, updated_by: user?.id ?? null }, { onConflict: 'key,locale' });
      if (error) throw error;
      setValues((v) => ({ ...v, [key]: value }));
      setStatus((s) => ({ ...s, [key]: 'saved' }));
      setTimeout(() => setStatus((s) => ({ ...s, [key]: undefined })), 1500);
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save');
      setStatus((s) => ({ ...s, [key]: undefined }));
    }
  };

  const triggerFaviconUpload = () => fileInputRef.current?.click();
  const handleFaviconFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingFavicon(true);
    try {
      const url = await uploadFaviconImage(file);
      setFaviconUrl(url);
      push('success', 'Favicon updated — live on the site immediately');
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to upload favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-[18px] font-bold text-ink">SEO & Analytics</h1>
        <p className="text-[12px] text-ink-faint mt-0.5">Tracking scripts, search console verification, and the browser-tab favicon</p>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <>
          <Card>
            <CardHeader title="Favicon" subtitle="The icon shown in the browser tab" icon={<ImageIcon className="w-4 h-4" />} />
            <CardBody>
              <input ref={fileInputRef} type="file" accept="image/png,image/x-icon,image/svg+xml" className="hidden" onChange={handleFaviconFileChange} />
              <div className="border border-border-subtle rounded-lg p-3 flex flex-col gap-2 w-fit">
                <div className="h-16 w-16 rounded-lg bg-surface-muted flex items-center justify-center overflow-hidden">
                  {faviconUrl ? (
                    <img src={faviconUrl} alt="Favicon" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="text-[10px] text-ink-faint text-center px-1">Not set — using default</span>
                  )}
                </div>
                <p className="text-[10px] text-ink-faint leading-snug max-w-40">PNG, ICO, or SVG — square, at least 32×32px (512×512px recommended). Not converted to WebP — kept in its original format for browser compatibility.</p>
                <Button size="sm" variant="secondary" onClick={triggerFaviconUpload} disabled={uploadingFavicon}>
                  <Upload className="w-3.5 h-3.5" /> {uploadingFavicon ? 'Uploading…' : 'Replace'}
                </Button>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Tracking & Verification" subtitle="Leave blank to disable" icon={<BarChart3 className="w-4 h-4" />} />
            <CardBody className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {TRACKING_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="flex items-center gap-1.5 text-[11px] font-semibold mb-1.5 text-ink-faint tracking-wide">
                    {f.label}
                    {status[f.key] === 'saving' && <span className="text-[10px] text-ink-faint">saving…</span>}
                    {status[f.key] === 'saved' && <span className="text-[10px] text-success">saved</span>}
                  </label>
                  <Input
                    placeholder={f.placeholder}
                    value={drafts[f.key] ?? ''}
                    onChange={(e) => handleChange(f.key, e.target.value)}
                    onBlur={() => handleBlur(f.key)}
                  />
                  <p className="text-[10px] text-ink-faint mt-1">{f.hint}</p>
                </div>
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Custom Head Code"
              subtitle="Raw HTML/script — pasted verbatim into <head> on every page"
              icon={<Search className="w-4 h-4" />}
            />
            <CardBody>
              <div>
                <label className="flex items-center gap-1.5 text-[11px] font-semibold mb-1.5 text-ink-faint tracking-wide">
                  Custom Head Code
                  {status['tracking.customHeadCode'] === 'saving' && <span className="text-[10px] text-ink-faint">saving…</span>}
                  {status['tracking.customHeadCode'] === 'saved' && <span className="text-[10px] text-success">saved</span>}
                </label>
                <Textarea
                  rows={6}
                  placeholder={'<meta name="..." content="..." />\n<script>...</script>'}
                  value={drafts['tracking.customHeadCode'] ?? ''}
                  onChange={(e) => handleChange('tracking.customHeadCode', e.target.value)}
                  onBlur={() => handleBlur('tracking.customHeadCode')}
                  className="font-mono text-[12px]"
                />
                <p className="text-[10px] text-ink-faint mt-1">
                  For anything not covered above — an extra verification meta tag, a chat widget, a second pixel. Runs once per
                  page load, same as the fields above. Paste tags only, no need to wrap in &lt;head&gt;.
                </p>
              </div>
            </CardBody>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-surface-muted">
              <Search className="w-4 h-4 text-ink-faint flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-ink-faint leading-snug">Per-page meta title/description live inside each page's own editor (Home Page, Products Page, Pages hub) — not here.</p>
            </div>
            <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-surface-muted">
              <ShieldCheck className="w-4 h-4 text-ink-faint flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-ink-faint leading-snug">robots.txt and sitemap.xml are generated automatically — nothing to configure here.</p>
            </div>
            <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-surface-muted">
              <Facebook className="w-4 h-4 text-ink-faint flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-ink-faint leading-snug">Pixel/GA4/GTM take a few minutes to start showing live traffic after saving.</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
